import { getOgmaSupportedLanguagesUri, getOwsSupportedLanguagesUri } from './utils/getOgmaUris';
import getOgmaSupportedLanguageTag from '../utils/getOgmaSupportedLanguageTag';
import setOgmaHeaders from './utils/setOgmaHeaders';
import SupportedLanguage from '../store/schema/SupportedLanguage';
import { isFeatureEnabled } from 'owa-feature-flags';
import getOwsHeaders from './utils/getOwsHeaders';
import { makeGetRequest } from 'owa-ows-gateway';

const timeout = 30000;

export default async function getSupportedLanguages(
    clientLocale: string,
    correlationId: string
): Promise<Map<string, SupportedLanguage>> {
    const promise = new Promise<Map<string, SupportedLanguage>>(function (resolve, reject) {
        if (isFeatureEnabled('rp-inlineTranslation-UseOWS')) {
            getSupportedLanguagesOWS(clientLocale, correlationId, resolve, reject);
        } else {
            getSupportedLanguagesOgma(clientLocale, correlationId, resolve, reject);
        }
    });
    return promise;
}

async function getSupportedLanguagesOWS(
    clientLocale: string,
    correlationId: string,
    resolve,
    reject
) {
    const headers = getOwsHeaders(correlationId);
    if (headers) {
        const ogmaSupportedLanguageTag = getOgmaSupportedLanguageTag(clientLocale);
        let timedOut: Boolean = false;
        const timeOutEvent = setTimeout(() => {
            timedOut = true;
            reject('Request timed out');
        }, timeout);

        try {
            const response = await makeGetRequest(
                getOwsSupportedLanguagesUri(ogmaSupportedLanguageTag),
                correlationId,
                false,
                headers,
                true
            );

            if (!timedOut) {
                clearTimeout(timeOutEvent);

                const languageMap = parseGetSupportedLanguagesResult(response);
                if (languageMap) {
                    resolve(languageMap);
                } else {
                    reject('OWS OGMA returned invalid response: ' + response);
                }
            }
        } catch (ex) {
            const exceptionMessage: string = ex?.message ? ex.message : '';
            reject('getSupportedLanguagesOWS failed:' + exceptionMessage);
        }
    } else {
        reject('getSupportedLanguagesOWS failed: headers were not set.');
    }
}

function getSupportedLanguagesOgma(clientLocale: string, correlationId: string, resolve, reject) {
    const xhttp = new XMLHttpRequest();
    xhttp.timeout = timeout;

    xhttp.onload = function () {
        if (xhttp.status === 200) {
            if (this.responseText) {
                const languageMap = parseGetSupportedLanguagesResult(JSON.parse(this.responseText));
                if (languageMap) {
                    resolve(languageMap);
                } else {
                    reject('OGMA returned invalid response: ' + this.response);
                }
            } else {
                reject('OGMA returned invalid response: ' + this.response);
            }
        } else {
            reject('OGMA failed to retrieve supported languages: ' + this.response);
        }
    };

    xhttp.onerror = function () {
        reject('OGMA call failed: ' + this.response);
    };

    xhttp.ontimeout = function () {
        reject('OGMA call timed out');
    };

    const ogmaSupportedLanguageTag = getOgmaSupportedLanguageTag(clientLocale);
    xhttp.open('GET', getOgmaSupportedLanguagesUri(ogmaSupportedLanguageTag));
    const headersAvailable = setOgmaHeaders(xhttp, correlationId);
    if (headersAvailable) {
        xhttp.send();
    } else {
        reject('getSupportedLanguagesOgma failed: headers were not set. ');
    }
}

function parseGetSupportedLanguagesResult(supportedLanguages: any): Map<string, SupportedLanguage> {
    try {
        const languageMap = new Map<string, SupportedLanguage>();
        supportedLanguages.forEach(function (supportedLanguage) {
            languageMap.set(supportedLanguage.LanguageTag, supportedLanguage);
        });
        return languageMap;
    } catch (error) {
        return null;
    }
}
